/**
 * Registry of reusable UrbanMap building blocks (components & base pages).
 * This is manually maintained — add entries when creating new shared pieces.
 */

import type { ComponentType } from 'react';

export interface UMRegistryEntry {
  slug: string;
  title: string;
  description: string;
  type: 'component' | 'page';
  /** Path relative to src/prototypes/urbanmap/ */
  path: string;
  /** Lazy loader for pages (components don't have one) */
  loader?: () => Promise<{ default: ComponentType }>;
}

export const UM_REGISTRY: UMRegistryEntry[] = [
  // ─── Reference components (mesurés sur urbanmap.myengie.com) ───────────────
  {
    slug: 'um-app-shell',
    title: 'UMAppShell',
    description: "Chrome fidèle de l'app : rail 80px, carte plein écran, barre d'outils, panneaux gauche/droite. Point de départ obligatoire.",
    type: 'component',
    path: '_components/UMAppShell.tsx',
  },
  {
    slug: 'um-map',
    title: 'UMMap',
    description: 'Carte Mapbox (styles de prod) avec fond hors ligne sans jeton, sources GeoJSON, clic sur objet, 2D/3D.',
    type: 'component',
    path: '_components/UMMap.tsx',
  },
  {
    slug: 'um-rail',
    title: 'UMRail',
    description: 'Rail de navigation gauche : Couches, Géométries, Mesures, Filtres, Sélection, Export, Import, Admin, Contact, Déconnexion.',
    type: 'component',
    path: '_components/UMRail.tsx',
  },
  {
    slug: 'um-map-toolbar',
    title: 'UMMapToolbar',
    description: 'Barre en haut à gauche : sélection de dossier, création de dossier, recherche, localisation.',
    type: 'component',
    path: '_components/UMMapToolbar.tsx',
  },
  {
    slug: 'um-map-controls',
    title: 'UMMapControls',
    description: 'Contrôles en bas à gauche : fond de carte, zoom, 2D/3D.',
    type: 'component',
    path: '_components/UMMapControls.tsx',
  },
  {
    slug: 'um-side-panel',
    title: 'UMSidePanel · UMLayerGroup · UMLegendItem',
    description: 'Panneau 300px (en-tête + fermer), groupes de couches avec case à cocher, légendes.',
    type: 'component',
    path: '_components/UMSidePanel.tsx',
  },
  {
    slug: 'um-reference-tokens',
    title: 'UMR (tokens de référence)',
    description: 'Couleurs et tailles mesurées, alias des tokens Fluid --nj-core-color-*. Couleurs cartographiques de prod.',
    type: 'component',
    path: '_components/referenceTokens.ts',
  },
  {
    slug: 'um-layers-panel',
    title: 'UMLayersPanel',
    description: 'Panneau « Couches de données » : catalogue exact des couches de prod et légendes.',
    type: 'component',
    path: '_components/UMLayersPanel.tsx',
  },
  {
    slug: 'um-rail-panels',
    title: 'UMGeometriesPanel · UMMeasuresPanel · UMSelectionPanel · UMExportPanel · UMImportPanel',
    description: 'Panneaux du rail : outils de géométrie, mesures, contenu de la sélection (résumé + outils), export (arbre de données), import (.xlsx).',
    type: 'component',
    path: '_components/UMRailPanels.tsx',
  },
  {
    slug: 'um-filters-panel',
    title: 'UMFiltersPanel',
    description: 'Panneau « Filtres des bâtiments » : 7 sections, champs et options exacts de prod, Réinitialiser.',
    type: 'component',
    path: '_components/UMFiltersPanel.tsx',
  },
  {
    slug: 'um-building-detail-panel',
    title: 'UMBuildingDetailPanel',
    description: 'Fiche bâtiment (modale latérale droite 300px) : identifiant, adresse, consommations, caractéristiques, propriétaire, sources.',
    type: 'component',
    path: '_components/UMBuildingDetailPanel.tsx',
  },
  {
    slug: 'um-form',
    title: 'UMForm (primitives Fluid v5)',
    description: 'Champs, min/max, select, radio, multi-sélection, segmented, curseur double, accordéon, message, boutons, badges, arbre de cases, info source.',
    type: 'component',
    path: '_components/UMForm.tsx',
  },
  {
    slug: 'um-tools',
    title: 'UMToolButton · UMToolGroup · UMDrawToolbar',
    description: "Boutons d'outils 80×64, groupes encadrés, barre de dessin 3×32.",
    type: 'component',
    path: '_components/UMTools.tsx',
  },

  // ─── Legacy components (palette approximative — ne pas utiliser pour un nouveau prototype) ──
  {
    slug: 'um-sidebar',
    title: 'UMSidebar',
    description: 'Barre latérale gauche avec icônes de navigation + panneau extensible.',
    type: 'component',
    path: '_components/UMSidebar.tsx',
  },
  {
    slug: 'um-building-panel',
    title: 'UMBuildingPanel',
    description: 'Panneau résumé bâtiment (droite) : énergie, caractéristiques, DPE.',
    type: 'component',
    path: '_components/UMBuildingPanel.tsx',
  },
  {
    slug: 'um-page-layout',
    title: 'UMPageLayout',
    description: 'Layout de base : PrototypeStudioLayout + UMSidebar + contenu flexible.',
    type: 'component',
    path: '_components/UMPageLayout.tsx',
  },
  {
    slug: 'um-tokens',
    title: 'UM Tokens',
    description: 'Palette de couleurs et design tokens UrbanMap (objet UM).',
    type: 'component',
    path: '_components/tokens.ts',
  },

  // ─── Pages de base ─────────────────────────────────────────────────────────
  {
    slug: 'home-reference',
    title: 'UrbanMap Home — référence fidèle',
    description: "Reproduction mesurée de l'accueil de prod (rail, barre d'outils, contrôles, panneau Couches). À copier pour tout nouveau prototype.",
    type: 'page',
    path: '_pages/home-reference/index.tsx',
    loader: () => import('./_pages/home-reference/index.tsx'),
  },
  {
    slug: 'home-map',
    title: 'UrbanMap Home (carte)',
    description: "Page d'accueil complète : carte Mapbox, sidebar, panneau bâtiment, onglets consommations/caractéristiques.",
    type: 'page',
    path: '_pages/home-map/index.tsx',
    loader: () => import('./_pages/home-map/index.tsx'),
  },
];
